/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useState } from "react";
import * as drive from "../api/drive";

// Module-level cache so records are fetched once per session, shared across mounts.
let cache = null;
let inflight = null;

/** Drop the cache after a mutation so new files show up in the picker. */
export const invalidateAssistantRecords = () => {
  cache = null;
  inflight = null;
};

/**
 * Lazily fetch the user's files + folders (once) for the assistant's @-mention
 * picker. Drive's equivalent of the accounts devices/orgs picker.
 *
 * Listing is flat and capped rather than a full tree walk: the picker is a
 * type-ahead over recent items, not a browser.
 */
export function useAssistantRecords() {
  const [records, setRecords] = useState(
    cache || { files: [], folders: [], loaded: false }
  );

  const load = useCallback(async () => {
    if (cache) {
      setRecords(cache);
      return cache;
    }
    if (!inflight) {
      inflight = Promise.allSettled([
        drive.listFiles({ pageSize: 100 }),
        drive.listFolders({ pageSize: 100 }),
      ]).then(([f, d]) => {
        cache = {
          files: f.status === "fulfilled" ? f.value?.files || [] : [],
          folders: d.status === "fulfilled" ? d.value?.folders || [] : [],
          loaded: true,
        };
        return cache;
      });
    }
    const result = await inflight;
    setRecords(result);
    return result;
  }, []);

  return { ...records, load };
}

/** Build a flat list of mention candidates matching `query`. */
export function mentionMatches(records, query) {
  const q = (query || "").toLowerCase();
  const folders = (records.folders || [])
    .filter((f) => (f.folder_name || "").toLowerCase().includes(q))
    .slice(0, 5)
    .map((f) => ({ type: "FOLDER", id: f.id, label: f.folder_name }));
  const files = (records.files || [])
    .filter((f) => (f.file_name || "").toLowerCase().includes(q))
    .slice(0, 5)
    .map((f) => ({ type: "FILE", id: f.id, label: f.file_name }));
  return [...folders, ...files];
}
